import { useMutation } from '@tanstack/react-query';
import { queryClient } from '@/queries';
import { QUERY_KEYS } from '@/lib/constants';
import {
    applyFreeMonthOffer,
    type ApplyOfferPayload,
    applyTwoMonthDiscountOffer,
    cancelPlatformSubscription,
} from '@/lib/api/settings/cancellation';

/**
 * Invalidate all billing-related queries after a cancellation flow action.
 * This ensures the UI updates properly after offers are accepted or subscription is cancelled.
 */
const invalidateBillingQueries = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SUBSCRIPTION_DETAILS] });
};

export const useApplyFreeMonthOffer = () => {
    const { mutateAsync, isPending } = useMutation({
        mutationFn: (data: ApplyOfferPayload) => applyFreeMonthOffer(data),
        onSuccess: invalidateBillingQueries,
    });

    return { mutateAsync, isPending };
};

export const useApplyTwoMonthDiscountOffer = () => {
    const { mutateAsync, isPending } = useMutation({
        mutationFn: (data: ApplyOfferPayload) => applyTwoMonthDiscountOffer(data),
        onSuccess: invalidateBillingQueries,
    });

    return { mutateAsync, isPending };
};

export const useCancelPlatformSubscription = () => {
    const { mutateAsync, isPending, isError } = useMutation({
        mutationFn: (subscriptionId: string) => cancelPlatformSubscription(subscriptionId),
        onSuccess: invalidateBillingQueries,
    });

    return { mutateAsync, isPending, isError };
};
